import { useLocation, useParams } from "react-router-dom"
import { Card, Tabs } from 'components/common';
import PIFHeader from "./PIFHeader"
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getPIF, getPifDiff, getDiffTargets, exportPifDiff } from "redux/crm/project/pifSlice";
import PIFActivities from "./PIFActivities";
import DetailGDS from "./detail/DetailGDS";
import DetailAreas from "./detail/DetailAreas";
import PIFChanges from "./detail/PIFChanges";
import PIFCompareBar from "./detail/PIFCompareBar";
import { PifDiffContext } from "./detail/PifDiffContext";
import { CRM_PIF_ISO } from "app_config";
import { FaChevronCircleUp, FaList, FaCalendarAlt, FaHistory, FaExchangeAlt } from "react-icons/fa";
const PIFDetail = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const [pif, setPif] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const sourceType = searchParams.get('type');
  const compareParam = searchParams.get('compare');

  const [against, setAgainst] = useState(compareParam || null);
  // Highlights ON by default: opening a revision/CO PIF, the point is to see what
  // changed. The diff is always fetched when a comparison exists (see below), so
  // defaulting on adds no extra load — it just renders the highlights immediately.
  // Inert on the first rev/CO (compare bar hidden, no changed paths) and toggleable.
  const [highlight, setHighlight] = useState(true);

  const { diffData, diffTargets, diffExporting } = useSelector((s) => s.crm_project_pif);

  // Memoised on the route ids so the fetch callbacks/effects below stay stable
  // across renders (a PIF id fixes its project/building/adaptation, so this
  // still changes only when params.id does).
  const ids = useMemo(() => ({
    projectId: params.project_id,
    buildingId: params.building_id,
    adaptationId: params.adaptation_id,
    pifId: params.id
  }), [params.project_id, params.building_id, params.adaptation_id, params.id]);

  // A response for a PIF the user already left (an email link opened while
  // another PIF was loading) must not replace the current one.
  const currentIdsRef = useRef(ids);
  currentIdsRef.current = ids;

  const reloadPif = useCallback(() => {
    dispatch(getPIF(ids)).then((action) => {
      if (currentIdsRef.current === ids) setPif(action.payload?.data?.attributes);
    });
  }, [dispatch, ids]);

  useEffect(() => {
    setPif(null);
    reloadPif();
    dispatch(getDiffTargets(ids));
  }, [dispatch, ids, reloadPif]);

  // First revision (REV#00) / first change order (CO#00) has nothing before it —
  // hide the compare bar + Changes tab to avoid confusing the user.
  const adaptation = pif?.adaptation;
  const isFirstAdaptation = Boolean(adaptation) && (
    (adaptation.type === 'revision' && String(adaptation.rev ?? '') === '00') ||
    (adaptation.type === 'change_order' && String(adaptation.co ?? '') === '00')
  );
  const showCompare = Boolean(pif) && !isFirstAdaptation;

  // Load the diff whenever the baseline changes (skip the first rev/CO).
  useEffect(() => {
    if (!pif || isFirstAdaptation) return;
    dispatch(getPifDiff({ ...ids, against }));
  }, [dispatch, ids, against, isFirstAdaptation, pif]);

  const changeByPath = useMemo(() => {
    const map = {};
    (diffData?.changes || []).forEach((c) => { map[c.path] = c; });
    return map;
  }, [diffData]);

  const diffCtx = useMemo(
    () => ({ changedPaths: diffData?.changed_paths || {}, changeByPath, enabled: highlight }),
    [diffData, changeByPath, highlight]
  );

  const handleExport = (format) => dispatch(exportPifDiff({ ...ids, against, format }));

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const changeCount = diffData?.summary?.total;

  return (
    <PifDiffContext.Provider value={diffCtx}>
      {/* Highlight style for changed fields (works regardless of Tailwind build). */}
      <style>{`
        .pif-diff-changed, .pif-diff-added, .pif-diff-removed{
          border-radius:4px;
          padding:1px 4px;
        }
        /* changed = amber, added = green, removed = rose — so the three kinds
           are distinguishable in place, matching the legend. */
        .pif-diff-changed{ background:#fef3c7 !important; box-shadow: inset 0 0 0 1.5px #f59e0b; }
        .pif-diff-added{   background:#d1fae5 !important; box-shadow: inset 0 0 0 1.5px #10b981; }
        .pif-diff-removed{ background:#ffe4e6 !important; box-shadow: inset 0 0 0 1.5px #f43f5e; }
        /* A highlighted value that renders empty is a field the baseline HAD a
           value for and this PIF cleared (a "removed" change). Many filled
           "(empty)" chips read as noise, so empty fields drop the fill — just a
           thin kind-coloured outline + a muted dash. The tooltip still reveals
           the old value. Only pure-empty value spans match :empty; spans with a
           unit suffix / label text are unaffected. */
        .pif-diff-changed:empty, .pif-diff-added:empty, .pif-diff-removed:empty{
          background:transparent !important;
          padding:0 3px;
        }
        .pif-diff-removed:empty{ box-shadow: inset 0 0 0 1px #fda4af; }
        .pif-diff-added:empty{   box-shadow: inset 0 0 0 1px #6ee7b7; }
        .pif-diff-changed:empty{ box-shadow: inset 0 0 0 1px #fcd34d; }
        .pif-diff-changed:empty::after, .pif-diff-added:empty::after, .pif-diff-removed:empty::after{
          content:'\\2014';
          color:#cbd5e1;
        }
        .pif-diff-changed input[type=checkbox]{ accent-color:#d97706; }
        .pif-diff-added input[type=checkbox]{ accent-color:#10b981; }
        .pif-diff-removed input[type=checkbox]{ accent-color:#f43f5e; }
        /* Rich-text / HTML fields (editor output is block <p>) can't be boxed as
           an inline span — the border renders as broken vertical bars. When the
           highlight wraps element children, render it as a block so the box
           surrounds the whole content; trim the editor's outer paragraph margins
           so the box stays tight. Plain-text / checkbox spans have no element
           child (:has(> *) fails) and stay inline. */
        .pif-diff-changed:has(> *), .pif-diff-added:has(> *), .pif-diff-removed:has(> *){
          display:block;
          padding:4px 8px;
        }
        .pif-diff-changed:has(> *) > :first-child,
        .pif-diff-added:has(> *) > :first-child,
        .pif-diff-removed:has(> *) > :first-child{ margin-top:0; }
        .pif-diff-changed:has(> *) > :last-child,
        .pif-diff-added:has(> *) > :last-child,
        .pif-diff-removed:has(> *) > :last-child{ margin-bottom:0; }
        /* changed table row */
        tr.pif-diff-row > td{ background:#fffbeb !important; }
        tr.pif-diff-row > td:first-child{ box-shadow: inset 3px 0 0 #f59e0b; }
        tr.pif-diff-row-added > td{ background:#ecfdf5 !important; }
        tr.pif-diff-row-added > td:first-child{ box-shadow: inset 3px 0 0 #10b981; }
        tr.pif-diff-row-removed > td{ background:#fef2f2 !important; }
        tr.pif-diff-row-removed > td:first-child{ box-shadow: inset 3px 0 0 #f43f5e; }
      `}</style>

      <PIFHeader pif={pif} source={sourceType} highlight={showCompare && highlight} against={against} onRestored={reloadPif} />

      <Card padding="none">
        {showCompare && (
          <PIFCompareBar
            targets={diffTargets}
            against={against}
            onAgainstChange={setAgainst}
            highlight={highlight}
            onToggleHighlight={() => setHighlight((v) => !v)}
            onExport={handleExport}
            diff={diffData}
            exporting={diffExporting}
          />
        )}

        <Tabs
          variant="underline"
          lazyMount
          navClassName="tw-px-3 tw-pt-2 tw-bg-white"
          contentClassName="tw-bg-slate-50 tw-p-4"
          tabs={[
            {
              label: 'General Data Sheet (GDS)',
              icon: <FaList size={13} />,
              content: <DetailGDS pif={pif} />
            },
            {
              label: 'Areas',
              icon: <FaCalendarAlt size={13} />,
              content: <DetailAreas data={pif} />
            },
            ...(showCompare ? [{
              label: changeCount ? `Changes (${changeCount})` : 'Changes',
              icon: <FaExchangeAlt size={13} />,
              content: <PIFChanges />
            }] : []),
            {
              label: 'Activities',
              icon: <FaHistory size={13} />,
              content: <PIFActivities />
            }
          ]}
        />

        <div className="tw-text-sm tw-m-2 tw-text-right">{CRM_PIF_ISO}</div>
      </Card>

      <div onClick={scrollToTop} style={styles.backToTopButton} title="Back to top">
        <FaChevronCircleUp />
      </div>
    </PifDiffContext.Provider>
  )
};

const styles = {
  backToTopButton: {
    position: 'fixed',
    bottom: '20px',
    right: '40px',
    cursor: 'pointer',
    fontSize: '26px',
    zIndex: '1000'
  }
};

export default PIFDetail;
